import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useLanguage } from "@/hooks/useLanguage";
import { generatePlan } from "@/lib/study";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Sparkles, Calendar, Check, Trash2 } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/planner")({
  component: PlannerPage,
  head: () => ({
    meta: [
      { title: "Study Planner - Inquira" },
      { name: "description", content: "Build an AI-generated day-by-day study plan for any exam or goal." },
      { property: "og:title", content: "Study Planner - Inquira" },
      { property: "og:description", content: "Set your goal, add concepts, get a personalized study schedule." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "https://www.inquira.online/planner" }],
  }),
});

interface Goal { id: string; title: string; exam_date: string | null; concepts: string[]; created_at: string }
interface Task { id: string; goal_id: string; scheduled_for: string; task: string; completed: boolean }

function PlannerPage() {
  const { user, isLoading, isPaid } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [goals, setGoals] = useState<Goal[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [title, setTitle] = useState("");
  const [examDate, setExamDate] = useState("");
  const [concepts, setConcepts] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => { if (!isLoading && !user) navigate({ to: "/login" }); }, [isLoading, user, navigate]);

  async function load() {
    if (!user) return;
    const [g, t] = await Promise.all([
      supabase
        .from("study_goals")
        .select("id, title, exam_date, concepts, created_at")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(50),
      supabase
        .from("study_tasks")
        .select("id, goal_id, scheduled_for, task, completed")
        .eq("user_id", user.id)
        .order("scheduled_for")
        .limit(500),
    ]);
    setGoals((g.data as Goal[]) ?? []);
    setTasks((t.data as Task[]) ?? []);
  }
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [user?.id]);

  async function createGoal() {
    if (!user) return;
    if (!isPaid) { toast.error("AI planner requires Pro."); navigate({ to: "/upgrade" }); return; }
    const t = title.trim();
    if (t.length < 2) return toast.error("Enter a title");
    const conceptsList = concepts.split(",").map((s) => s.trim()).filter(Boolean).slice(0, 20);
    setCreating(true);
    try {
      const { data: goal, error: gErr } = await supabase.from("study_goals").insert({
        user_id: user.id, title: t, exam_date: examDate || null, concepts: conceptsList,
      }).select("*").single();
      if (gErr) throw gErr;
      const g = goal as Goal;
      const { tasks: gen } = await generatePlan(t, examDate || null, conceptsList, language.code);
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const rows = gen.map((x) => {
        const d = new Date(today); d.setDate(d.getDate() + x.day_offset);
        return { goal_id: g.id, user_id: user.id, scheduled_for: d.toISOString().slice(0, 10), task: x.task.slice(0, 500) };
      });
      if (rows.length) {
        const { error: tErr } = await supabase.from("study_tasks").insert(rows);
        if (tErr) throw tErr;
      }
      toast.success(`Plan created with ${rows.length} tasks`);
      setTitle(""); setExamDate(""); setConcepts("");
      load();
    } catch (e) { toast.error((e as Error).message); }
    finally { setCreating(false); }
  }

  async function toggle(t: Task) {
    const { error } = await supabase.from("study_tasks").update({ completed: !t.completed }).eq("id", t.id);
    if (error) return toast.error(error.message);
    setTasks(tasks.map((x) => x.id === t.id ? { ...x, completed: !x.completed } : x));
  }

  async function deleteGoal(id: string) {
    if (!confirm("Delete this goal and all its tasks?")) return;
    const { error } = await supabase.from("study_goals").delete().eq("id", id);
    if (error) return toast.error(error.message);
    load();
  }

  if (isLoading || !user) return <main className="min-h-screen bg-background p-6"><p className="text-muted-foreground">Loading…</p></main>;

  return (
    <main className="min-h-screen bg-background">
      <header className="sticky top-0 z-10 border-b border-border bg-background/95 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center justify-between p-4">
          <Link to="/" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" /> Home
          </Link>
          <h1 className="text-lg font-bold">Study Planner</h1>
          <span className="text-xs text-muted-foreground">{goals.length} goals</span>
        </div>
      </header>

      <div className="mx-auto max-w-3xl space-y-6 p-4">
        <section className="rounded-2xl border border-border bg-card p-4">
          <label className="mb-2 flex items-center gap-2 text-sm font-semibold">
            <Sparkles className="h-4 w-4 text-primary" /> Create a new plan
          </label>
          <div className="space-y-2">
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Goal (e.g. AP Calc BC exam)"
              maxLength={200} className="w-full rounded-lg border border-border bg-background p-2 text-sm" />
            <input type="date" value={examDate} onChange={(e) => setExamDate(e.target.value)}
              min={new Date().toISOString().slice(0, 10)} className="w-full rounded-lg border border-border bg-background p-2 text-sm" />
            <input value={concepts} onChange={(e) => setConcepts(e.target.value)}
              placeholder="Concepts (comma-separated): derivatives, integrals, series"
              className="w-full rounded-lg border border-border bg-background p-2 text-sm" />
            <Button onClick={createGoal} disabled={creating} className="w-full">
              {creating ? "Building plan…" : "Generate plan"}
            </Button>
            {!isPaid && <p className="text-xs text-muted-foreground">Pro feature. <Link to="/upgrade" className="underline">Upgrade</Link>.</p>}
          </div>
        </section>

        {goals.map((g) => {
          const gt = tasks.filter((t) => t.goal_id === g.id);
          const done = gt.filter((t) => t.completed).length;
          return (
            <section key={g.id} className="rounded-2xl border border-border bg-card p-4">
              <div className="mb-3 flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-semibold">{g.title}</h3>
                  <p className="text-xs text-muted-foreground">
                    {g.exam_date && <><Calendar className="mr-1 inline h-3 w-3" />{new Date(g.exam_date).toLocaleDateString()} · </>}
                    {done}/{gt.length} done
                  </p>
                </div>
                <button onClick={() => deleteGoal(g.id)} className="text-muted-foreground hover:text-destructive">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <div className="space-y-1">
                {gt.map((t) => (
                  <button key={t.id} onClick={() => toggle(t)}
                    className={`flex w-full items-start gap-2 rounded-lg border p-2 text-left text-sm ${t.completed ? "border-border bg-muted opacity-60 line-through" : "border-border bg-background"}`}>
                    <span className={`mt-0.5 flex h-4 w-4 flex-none items-center justify-center rounded border ${t.completed ? "border-primary bg-primary" : "border-border"}`}>
                      {t.completed && <Check className="h-3 w-3 text-primary-foreground" />}
                    </span>
                    <span className="flex-1">
                      <span className="mr-2 text-xs text-muted-foreground">{new Date(t.scheduled_for).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}</span>
                      {t.task}
                    </span>
                  </button>
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </main>
  );
}